import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IOeDataOrganizationDTO, IOeDataOrganizationsDAO } from '../../../api/oe-data-organizations.dao';
import { OeDataOrganizationsDAO } from '../../../api/implementations/oe-data-organizations.dao';
import { IPaginatedContentResponse } from '../../../api/shared.dto';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { IReactIntlProvider } from '../../../infrastructure/react-intl-provider';
import { DEFAULT_PAGE_SIZE } from '../../../constants/pagination.contants';
import { alertServerError } from '../../global/modal/alerts-host/alert-templates';
import { PagingViewModel } from '../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { IItemProps } from '../../global/controlled-multi-selection-dropdown/controlled-multi-selection-dropdown';
import {
    DEFAULT_OE_DATA_ORGANIZATION_COLUMNS,
    MANDATORY_OE_DATA_ORGANIZATION_COLUMN_ID,
    OE_DATA_ORGANIZATION_COLUMN_TITLES,
    OE_DATA_ORGANIZATION_COLUMNS,
    OE_DATA_ORGANIZATIONS_COLUMNS_STORAGE_KEY,
    TOeDataOrganizationColumnId
} from './oe-data-organization-columns';

export class OeDataOrganizationsVM {
    @mobx.observable
    private organizations: IOeDataOrganizationDTO[] = [];

    @mobx.observable
    private fetchingOrganizations: boolean = false;

    @mobx.observable
    private visibleColumns: TOeDataOrganizationColumnId[] = DEFAULT_OE_DATA_ORGANIZATION_COLUMNS;

    private paginationVM: PagingViewModel;
    private readonly pageReactionDisposer: mobx.IReactionDisposer;

    constructor(
        @IoC.Inject private readonly oeDataOrganizationsDAO: IOeDataOrganizationsDAO = IoC.Container.get(OeDataOrganizationsDAO),
        @IoC.Inject private readonly errorParsing: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.paginationVM = new PagingViewModel({ pageSize: DEFAULT_PAGE_SIZE });
        this.initVisibleColumns();

        this.pageReactionDisposer = mobx.reaction(this.paginationVM.getCurrentPage, this.fetchOrganizations);
    }

    public getPagingVM(): PagingViewModel {
        return this.paginationVM;
    }

    public isFetchingOrganizations(): boolean {
        return this.fetchingOrganizations;
    }

    public getOrganizations(): IOeDataOrganizationDTO[] {
        return this.organizations;
    }

    public getVisibleColumns(): TOeDataOrganizationColumnId[] {
        return this.visibleColumns;
    }

    @mobx.computed
    public get visibleColumnItems(): IItemProps[] {
        return this.getColumnItems(this.visibleColumns);
    }

    @mobx.computed
    public get defaultColumnItems(): IItemProps[] {
        return this.getColumnItems(DEFAULT_OE_DATA_ORGANIZATION_COLUMNS);
    }

    public getColumnTitle(columnId: TOeDataOrganizationColumnId): string {
        return this.intlProvider.getIntl().formatMessage(OE_DATA_ORGANIZATION_COLUMN_TITLES[columnId]);
    }

    public fetchColumnItems = (): Promise<IItemProps[]> => {
        return Promise.resolve(this.getColumnItems(OE_DATA_ORGANIZATION_COLUMNS));
    };

    @mobx.action.bound
    public applyVisibleColumns(columns: IItemProps[]): void {
        const visibleColumns = this.getSanitizedColumns(columns.map(column => column.id));
        localStorage.setItem(OE_DATA_ORGANIZATIONS_COLUMNS_STORAGE_KEY, visibleColumns.join());
        this.visibleColumns = visibleColumns;
    }

    public disposeReactions = (): void => {
        this.pageReactionDisposer();
    };

    @mobx.action
    public fetchOrganizations = async (): Promise<void> => {
        this.fetchingOrganizations = true;
        try {
            const result = await this.oeDataOrganizationsDAO.getOrganizations({
                size: this.paginationVM.getPageSize(),
                page: this.paginationVM.getCurrentPageForBE(),
            });
            this.onFetchSuccess(result);
        } catch (error) {
            this.onFetchFail(error);
        }
    };

    @mobx.action
    private initVisibleColumns(): void {
        const storedColumns = localStorage.getItem(OE_DATA_ORGANIZATIONS_COLUMNS_STORAGE_KEY);
        if (storedColumns != null) {
            this.visibleColumns = this.getSanitizedColumns(storedColumns.split(','));
        }
    }

    private getColumnItems(columnIds: TOeDataOrganizationColumnId[]): IItemProps[] {
        return columnIds
            .filter(columnId => columnId !== MANDATORY_OE_DATA_ORGANIZATION_COLUMN_ID)
            .map(columnId => ({ id: columnId, label: this.getColumnTitle(columnId) }));
    }

    private getSanitizedColumns(columnIds: string[]): TOeDataOrganizationColumnId[] {
        const selectedColumnIds = new Set(columnIds);
        if (columnIds.length && !OE_DATA_ORGANIZATION_COLUMNS.some(columnId => selectedColumnIds.has(columnId))) {
            return DEFAULT_OE_DATA_ORGANIZATION_COLUMNS;
        }

        return OE_DATA_ORGANIZATION_COLUMNS
            .filter(columnId => columnId === MANDATORY_OE_DATA_ORGANIZATION_COLUMN_ID || selectedColumnIds.has(columnId));
    }

    @mobx.action
    private onFetchSuccess(data: IPaginatedContentResponse<IOeDataOrganizationDTO>): void {
        this.organizations = data.content;
        this.fetchingOrganizations = false;
        this.paginationVM.setTotalRecords(data.totalElements);
    }

    @mobx.action
    private onFetchFail(error: unknown): void {
        this.fetchingOrganizations = false;
        const errorMessage = this.errorParsing.extractMessage(error);
        alertServerError('API Error fetching OE Data organizations', errorMessage);
    }
}
